/**
 * The frame. Draw order:
 *
 *   1. static floor (ground, walls, flat props): cached bitmaps, in chunks for the town
 *   2. live floor effects (water, dance floor)
 *   3. depth-sorted standing things: shared prop sprites, live props, blobs, NPCs, critters
 *   4. night: a tint over what is drawn, then additive light pools
 *   5. the sky behind it all (destination-over), in screen space
 *   6. names, bubbles and labels, crisp and unlit, in screen space
 *
 * Only what is on screen is drawn or cached. Props that look alike share one
 * sprite. Caches are rendered at the exact device scale so blits are 1:1;
 * during a zoom they are stretched, and re-rendered a few per frame once it
 * settles, so there is never a hitch or a blank chunk.
 */
import { NPCS } from '../../../shared/npcs.ts'
import { cellAt, footprint, grid, groundAt, KINDS, ROOMS, SEAT, type Ambience, type Prop, type RoomDef } from '../../../shared/world.ts'
import type { Player, World } from '../game/state.ts'
import { blobArt, blobMotion, drawBlob } from './blob.ts'
import { alpha, mix, shade } from './color.ts'
import { critterAt, drawCritter } from './critters.ts'
import { blobShadow, glowAt, iso, poly, rnd, roundRect, TH, TW, wrapText, type Pt } from './iso.ts'
import { lighthouseBeam } from './buildings.ts'
import { drawAir, drawFireflies, drawFloatingLanterns, emitters, type Emitter } from './ambience.ts'
import { drawFlatLive, drawProp, HEIGHT, isLive, LIVE, propBounds, propLights, spriteKey, variantOf, type Light } from './props.ts'
import { drawArch, drawStatic, portalNeedsArch, staticBounds, type Region } from './room.ts'

const FONT = 'Nunito, ui-rounded, "SF Pro Rounded", "Segoe UI", system-ui, sans-serif'
/** Rendered height of a blob's 100-unit viewBox at zoom 1. */
export const BLOB = 70
const SEAT_Z: Record<string, number> = { chair: 0.42, sofa: 0.42, bench: 0.42, stump: 0.36, beanbag: 0.2, picnic: 0, towel: 0, cushion: 0.12, stool: 0.4 }
const CHUNK = 12
const MAX_CHUNKS = 24
const MAX_SPRITES = 220

export interface Camera { x: number; y: number; zoom: number }
interface Box { x: number; y: number; w: number; h: number }
interface Cache { canvas: HTMLCanvasElement; x: number; y: number; scale: number; day: number; used: number }
interface Chunk { reg: Region; box: Box; clip?: Pt[]; cache?: Cache }
interface PropItem { p: Prop; box: Box; tight: Box; k: number; live: boolean; key: string; ox: number; oy: number; seeThrough: number }

export type TimeMode = 'auto' | 'day' | 'night'

export function daylight(mode: TimeMode, date = new Date()): number {
  if (mode === 'day') return 1
  if (mode === 'night') return 0
  const h = date.getHours() + date.getMinutes() / 60
  if (h >= 7.5 && h <= 17.5) return 1
  if (h <= 5.5 || h >= 20) return 0
  return h < 12 ? (h - 5.5) / 2 : 1 - (h - 17.5) / 2.5
}

export interface Overlay {
  hover?: { x: number; y: number }
  /** Player or NPC under the pointer, for a highlight. */
  focus?: string
  names: boolean
  calm: boolean
  npcs: Player[]
}

const hit = (a: Box, b: Box) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h

export class Scene {
  readonly ctx: CanvasRenderingContext2D
  readonly canvas: HTMLCanvasElement
  cam: Camera = { x: 0, y: 0, zoom: 1 }
  /** User zoom multiplier on top of the room's base zoom. */
  userZoom = 1
  timeMode: TimeMode = 'auto'
  private w = 1
  private h = 1
  private dpr = 1
  private room?: RoomDef
  private chunks: Chunk[] = []
  private items: PropItem[] = []
  private sprites = new Map<string, Cache>()
  private lights: Light[] = []
  private roles = new Map<string, string>()
  /** Seat tile index → the seat height of what is there. */
  private seatZ = new Map<number, number>()
  /** Water tiles, and the shoreline ones, for the shimmer and the foam. */
  private water: Array<[number, number, boolean]> = []
  private beams: Prop[] = []
  private amb: Ambience[] = []
  private emit: Emitter[] = []
  private zoomChangedAt = 0
  private lastZoom = 0
  private placed = false
  /** Screen positions of heads from the last frame, for picking and bubbles. */
  readonly heads = new Map<string, Pt>()

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('2D canvas unavailable')
    this.ctx = ctx
  }

  resize(w: number, h: number): void {
    this.dpr = Math.min(2, window.devicePixelRatio || 1)
    this.w = w
    this.h = h
    this.canvas.width = Math.round(w * this.dpr)
    this.canvas.height = Math.round(h * this.dpr)
  }

  setRoom(r: RoomDef): void {
    if (this.room === r) return
    this.room = r
    this.placed = false
    // Chunks: the town is cut into squares; a small room is one piece with its walls.
    this.chunks = []
    if (r.outdoor && r.w * r.d > 400) {
      const S = 0.65
      for (let x0 = 0; x0 < r.w; x0 += CHUNK) for (let y0 = 0; y0 < r.d; y0 += CHUNK) {
        const reg = { x0, y0, x1: Math.min(r.w, x0 + CHUNK), y1: Math.min(r.d, y0 + CHUNK) }
        const zx = reg.x1 === r.w ? -S : 0, zy = reg.y1 === r.d ? -S : 0
        // Clip a hair outside the region (and draw a tile beyond it) so neighbouring chunks overlap: no seams.
        const e = 0.03, a0 = reg.x0 - e, b0 = reg.y0 - e, a1 = reg.x1 + e, b1 = reg.y1 + e
        const clip = [iso(a0, b0), iso(a1, b0), iso(a1, b0, zx), iso(a1, b1, Math.min(zx, zy)), iso(a0, b1, zy), iso(a0, b1)]
        this.chunks.push({ reg, box: staticBounds(r, reg), clip })
      }
    } else {
      this.chunks.push({ reg: { x0: 0, y0: 0, x1: r.w, y1: r.d }, box: staticBounds(r) })
    }
    this.items = r.props.filter((p) => !KINDS[p.kind].flat).map((p) => {
      const { w, d } = footprint(p)
      const o = iso(Math.floor(p.x), Math.floor(p.y))
      const h = HEIGHT[p.kind] ?? 1
      const a = iso(p.x, p.y + d, 0), b = iso(p.x + w, p.y, 0), top = iso(p.x, p.y, h), bot = iso(p.x + w, p.y + d, 0)
      const pad = p.kind === 'tree' || p.kind === 'palm' || p.kind === 'pine' ? 16 : 0
      return {
        p, box: propBounds(p), tight: { x: a.x - pad, y: top.y - pad, w: b.x - a.x + pad * 2, h: bot.y - top.y + pad },
        k: p.x + w / 2 + p.y + d / 2, live: isLive(p), key: spriteKey(p), ox: o.x, oy: o.y,
        seeThrough: p.kind === 'building' ? (p.style === 'tower' ? 0.2 : 0.35) : h >= 2 ? 0.5 : 1,
      }
    })
    this.lights = r.props.flatMap(propLights)
    this.roles = new Map(NPCS[r.id].map((n) => [n.id, n.role]))
    const g = grid(r.id)
    this.seatZ.clear()
    for (const p of r.props) {
      if (!KINDS[p.kind].seat) continue
      const f = footprint(p)
      for (let x = Math.floor(p.x); x < Math.ceil(p.x + f.w); x++) for (let y = Math.floor(p.y); y < Math.ceil(p.y + f.d); y++) this.seatZ.set(y * r.w + x, SEAT_Z[p.kind] ?? 0.3)
    }
    this.water = []
    for (let y = 0; y < r.d; y++) for (let x = 0; x < r.w; x++) {
      const k = groundAt(g, x, y)
      if (k !== 'canal' && k !== 'sea') continue
      const shore = k === 'sea' && !['canal', 'sea', 'bridge'].includes(groundAt(g, x, y - 1))
      this.water.push([x, y, shore])
    }
    this.beams = r.props.filter((p) => p.kind === 'building' && p.style === 'lighthouse')
    this.amb = r.ambience ?? []
    this.emit = emitters(r.props)
  }

  /** The room's base zoom: small rooms fit the screen; the town is explored up close. */
  private baseZoom(): number {
    const r = this.room!
    const small = Math.min(this.w, this.h) < 560
    if (r.w * r.d > 400) return small ? 0.8 : 1
    const b = staticBounds(r)
    const fit = Math.min(this.w / b.w, (this.h - 40) / b.h) * 1.18
    return Math.max(small ? 0.62 : 0.7, Math.min(1.5, fit))
  }

  // ─── Coordinates ─────────────────────────────────────────────────────────

  toScreen(p: Pt): Pt {
    return { x: (p.x - this.cam.x) * this.cam.zoom + this.w / 2, y: (p.y - this.cam.y) * this.cam.zoom + this.h / 2 }
  }

  toTile(sx: number, sy: number): { x: number; y: number } {
    const wx = (sx - this.w / 2) / this.cam.zoom + this.cam.x
    const wy = (sy - this.h / 2) / this.cam.zoom + this.cam.y
    const a = wx / (TW / 2), b = wy / (TH / 2)
    return { x: (a + b) / 2, y: (b - a) / 2 }
  }

  /** The blob (player or NPC) under a screen point, if any. */
  pick(sx: number, sy: number): string | undefined {
    let best: string | undefined, bd = Infinity
    const r = BLOB * this.cam.zoom * 0.42
    for (const [id, h] of this.heads) {
      const d = Math.hypot(h.x - sx, h.y + r * 0.9 - sy)
      if (d < r && d < bd) { bd = d; best = id }
    }
    return best
  }

  // ─── Caches ─────────────────────────────────────────────────────────────

  private renderChunk(c: Chunk, scale: number, day: number, now: number): void {
    const b = c.box
    const s = Math.min(scale, 4096 / Math.max(b.w, b.h))
    const cv = c.cache?.canvas ?? document.createElement('canvas')
    cv.width = Math.ceil(b.w * s)
    cv.height = Math.ceil(b.h * s)
    const g = cv.getContext('2d')!
    g.setTransform(s, 0, 0, s, -b.x * s, -b.y * s)
    if (c.clip) { poly(g, c.clip); g.clip() }
    const r = this.room!
    const grow = c.clip ? 1 : 0
    drawStatic(g, r, day, { x0: Math.max(0, c.reg.x0 - grow), y0: Math.max(0, c.reg.y0 - grow), x1: Math.min(r.w, c.reg.x1 + grow), y1: Math.min(r.d, c.reg.y1 + grow) })
    c.cache = { canvas: cv, x: b.x, y: b.y, scale: s, day, used: now }
  }

  private sprite(it: PropItem, scale: number, day: number, now: number, budget: { n: number }): Cache | undefined {
    const have = this.sprites.get(it.key)
    const fresh = have && Math.abs(have.scale - scale) < 1e-3 && have.day === day
    if (have && (fresh || budget.n <= 0)) { have.used = now; return have }
    budget.n--
    // Drawn at the origin tile; iso() is linear, so one sprite serves every copy.
    const ix = Math.floor(it.p.x), iy = Math.floor(it.p.y)
    const q: Prop = { ...it.p, x: it.p.x - ix, y: it.p.y - iy }
    const pb = propBounds(q)
    const cv = have?.canvas ?? document.createElement('canvas')
    cv.width = Math.ceil(pb.w * scale)
    cv.height = Math.ceil(pb.h * scale)
    const g = cv.getContext('2d')!
    g.setTransform(scale, 0, 0, scale, -pb.x * scale, -pb.y * scale)
    drawProp(g, q, 0, day, variantOf(it.p))
    const c = { canvas: cv, x: pb.x, y: pb.y, scale, day, used: now }
    this.sprites.set(it.key, c)
    if (this.sprites.size > MAX_SPRITES) {
      let old: string | undefined, t = Infinity
      for (const [k, v] of this.sprites) if (v.used < t) { t = v.used; old = k }
      if (old) this.sprites.delete(old)
    }
    return c
  }

  private blit(c: Cache, dx = 0, dy = 0): void {
    this.ctx.drawImage(c.canvas, c.x + dx, c.y + dy, c.canvas.width / c.scale, c.canvas.height / c.scale)
  }

  // ─── Frame ──────────────────────────────────────────────────────────────

  frame(world: World, now: number, o: Overlay): void {
    const r = this.room
    if (!r) return
    const ctx = this.ctx
    const t = now / 1000
    const day = Math.round(daylight(this.timeMode) * 25) / 25

    // Camera: follow our blob, keep the room on screen.
    const me = world.self
    const target = this.baseZoom() * this.userZoom
    this.cam.zoom += (target - this.cam.zoom) * 0.18
    if (Math.abs(target - this.cam.zoom) < 0.002) this.cam.zoom = target
    const b = staticBounds(r)
    let tx = b.x + b.w / 2, ty = b.y + b.h / 2
    const meD = me ? world.derive(me, now) : undefined
    if (meD) { const p = iso(meD.x, meD.y, 0.8); tx = p.x; ty = p.y }
    const vw = this.w / this.cam.zoom, vh = this.h / this.cam.zoom
    const clamp = (v: number, lo: number, len: number, view: number) => (len <= view ? lo + len / 2 : Math.max(lo + view / 2, Math.min(lo + len - view / 2, v)))
    tx = clamp(tx, b.x, b.w, vw)
    ty = clamp(ty, b.y - 30, b.h + 30, vh)
    if (!this.placed && me) { this.cam.x = tx; this.cam.y = ty; this.placed = true }
    this.cam.x += (tx - this.cam.x) * 0.12
    this.cam.y += (ty - this.cam.y) * 0.12

    const scale = this.dpr * this.cam.zoom
    if (Math.abs(this.cam.zoom - this.lastZoom) > 1e-4) { this.zoomChangedAt = now; this.lastZoom = this.cam.zoom }
    const settled = now - this.zoomChangedAt > 160
    const view: Box = { x: this.cam.x - vw / 2 - 60, y: this.cam.y - vh / 2 - 60, w: vw + 120, h: vh + 160 }

    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.globalCompositeOperation = 'source-over'
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    ctx.setTransform(scale, 0, 0, scale, this.dpr * (this.w / 2 - this.cam.x * this.cam.zoom), this.dpr * (this.h / 2 - this.cam.y * this.cam.zoom))

    // 1–2. Floor: visible chunks; stale ones re-render a couple per frame (all at once on arrival).
    let chunkBudget = this.chunks.some((c) => c.cache) ? 2 : 99
    for (const c of this.chunks) {
      if (!hit(c.box, view)) continue
      const want = Math.min(scale, 4096 / Math.max(c.box.w, c.box.h))
      const stale = !c.cache || c.cache.day !== day || (settled && Math.abs(c.cache.scale - want) > 1e-3)
      if (stale && (chunkBudget-- > 0 || !c.cache)) this.renderChunk(c, scale, day, now)
      c.cache!.used = now
      this.blit(c.cache!)
    }
    this.evictChunks()
    for (const p of r.props) if (KINDS[p.kind].flat && LIVE.has(p.kind)) drawFlatLive(ctx, p, t, day)
    this.waterLive(view, t, day)
    drawFloatingLanterns(ctx, this.amb, this.water, view, t, day)
    if (o.hover) this.hoverTile(o.hover.x, o.hover.y)

    // 3. Depth-sorted standing things.
    const g = grid(r.id)
    const items: Array<{ k: number; draw: () => void }> = []
    const meK = meD ? meD.x + meD.y : -Infinity
    const mePt = meD ? iso(meD.x, meD.y, 0.6) : undefined
    const budget = { n: settled ? 40 : 0 }
    for (const it of this.items) {
      if (!hit(it.box, view)) continue
      // Walk behind something tall and it goes see-through, so you never lose your blob.
      const behind = mePt && it.seeThrough < 1 && it.k > meK + 0.3 && mePt.x > it.tight.x && mePt.x < it.tight.x + it.tight.w && mePt.y > it.tight.y && mePt.y < it.tight.y + it.tight.h
      const draw = it.live
        ? () => drawProp(ctx, it.p, t, day)
        : () => {
            const c = this.sprite(it, settled ? scale : this.sprites.get(it.key)?.scale ?? scale, day, now, budget)
            if (c) this.blit(c, it.ox, it.oy)
          }
      items.push({ k: it.k, draw: behind ? () => { ctx.globalAlpha = it.seeThrough; draw(); ctx.globalAlpha = 1 } : draw })
    }
    for (const p of r.portals) if (portalNeedsArch(r, p)) items.push({ k: p.x + p.y + 1.05, draw: () => drawArch(ctx, r, p, ROOMS[p.to].accent, t) })
    r.critters.forEach((c, i) => {
      const cp = critterAt(c, t, i)
      const pt = iso(cp.x, cp.y)
      if (pt.x < view.x || pt.x > view.x + view.w || pt.y < view.y || pt.y > view.y + view.h) return
      // Boats slip under bridges: hidden while their bow is beneath a deck.
      if (c.kind === 'boat' && groundAt(g, Math.floor(cp.x), Math.floor(cp.y)) === 'bridge') return
      items.push({ k: cp.x + cp.y + (c.kind === 'butterfly' ? 0.6 : 0), draw: () => drawCritter(ctx, c, cp, t) })
    })
    this.heads.clear()
    const placed: Array<{ p: Player; foot: Pt; lift: number; npc: boolean; dist: number }> = []
    const everyone: Array<[Player, boolean]> = []
    for (const p of world.players.values()) if (!world.hidden.has(p.id)) everyone.push([p, false])
    for (const p of o.npcs) everyone.push([p, true])
    for (const [p, npc] of everyone) {
      const d = world.derive(p, now)
      let z = 0
      if (d.seat) z = this.seatZ.get(Math.floor(d.y) * r.w + Math.floor(d.x)) ?? 0.3
      const foot = iso(d.x, d.y, z)
      if (foot.x < view.x || foot.x > view.x + view.w || foot.y < view.y || foot.y > view.y + view.h + 60) continue
      const motion = blobMotion(d.state, t, p.phase, world.actOf(p, now), o.calm)
      const art = blobArt(p.look, world.emoteOf(p, now))
      const k = d.x + d.y + (d.seat ? 0.06 : 0) + (cellAt(g, Math.floor(d.x), Math.floor(d.y)) === SEAT ? 0.02 : 0)
      items.push({
        k,
        draw: () => {
          if (z < 0.05) blobShadow(ctx, d.x, d.y, 0.36 * (1 + motion.dy / 80), 0.24)
          else { const sp = iso(d.x, d.y, z); ctx.fillStyle = 'rgba(40,28,64,0.18)'; ctx.beginPath(); ctx.ellipse(sp.x, sp.y, 12, 5, 0, 0, Math.PI * 2); ctx.fill() }
          if (o.focus === p.id || p.id === world.me) this.ring(d.x, d.y, z, p.id === world.me ? '#ffffff' : art.color, t)
          drawBlob(ctx, art, foot.x, foot.y, { size: BLOB, facing: d.face, sx: motion.sx, sy: motion.sy, rot: motion.rot, dx: motion.dx, dyExtra: motion.dy, eyes: motion.eyes, hat: p.look.hat, t: t + p.phase })
        },
      })
      placed.push({ p, foot, lift: motion.dy, npc, dist: meD ? Math.hypot(d.x - meD.x, d.y - meD.y) : 0 })
    }
    items.sort((a, b2) => a.k - b2.k)
    for (const it of items) it.draw()
    drawAir(ctx, this.amb, this.emit, view, t)

    // 4. Night.
    const night = 1 - day
    if (night > 0.01) {
      ctx.save()
      ctx.setTransform(1, 0, 0, 1, 0, 0)
      ctx.globalCompositeOperation = 'source-atop'
      ctx.fillStyle = `rgba(16,18,54,${night * (r.outdoor ? 0.5 : 0.32)})`
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
      ctx.restore()
      ctx.save()
      for (const l of this.lights) {
        const p = iso(l.x, l.y, 0)
        if (p.x < view.x - l.r || p.x > view.x + view.w + l.r || p.y < view.y - l.r || p.y > view.y + view.h + l.r) continue
        const fl = l.flicker ? 1 - l.flicker * 0.25 * (0.5 + 0.5 * Math.sin(t * 9 + l.x * 7) * Math.sin(t * 5.3 + l.y)) : 1
        const a = l.a * night * fl
        glowAt(ctx, p, l.r, l.r * 0.5, l.color, a * 0.8)
        glowAt(ctx, iso(l.x, l.y, l.z), l.r * 0.35, l.r * 0.35, l.color, a)
      }
      for (const b of this.beams) lighthouseBeam(ctx, b, t, day)
      drawFireflies(ctx, this.amb, view, t, night)
      // Blobs glow faintly in their own colour after dark, so a pale one never sinks into the paving.
      for (const { p, foot } of placed) glowAt(ctx, { x: foot.x, y: foot.y - BLOB * 0.3 }, BLOB * 0.55, BLOB * 0.5, blobArt(p.look).color, 0.3 * night)
      ctx.restore()
    }

    // 5. Sky.
    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.globalCompositeOperation = 'destination-over'
    this.sky(day, t, r)
    ctx.restore()

    // 6. Overlays, in CSS px.
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    ctx.globalCompositeOperation = 'source-over'
    const zoom = this.cam.zoom
    for (const { p, foot, lift } of placed) {
      const s = this.toScreen(foot)
      this.heads.set(p.id, { x: s.x, y: s.y - (BLOB * 0.78 - lift * 0.6 * BLOB / 100) * zoom })
    }
    for (const p of r.portals) {
      // Town doors announce themselves when you are close; exits indoors always do.
      if (r.outdoor && (!meD || Math.hypot(meD.x - p.x, meD.y - p.y) > 6)) continue
      const s = this.toScreen(iso(p.x + 0.5, p.y + 0.5, r.outdoor ? 0 : 2.3))
      this.chip(s.x, r.outdoor ? s.y + 28 : s.y, r.outdoor ? `Enter ${ROOMS[p.to].name}` : `→ ${p.label}`, ROOMS[p.to].accent, false)
    }
    const players = placed.filter((q) => !q.npc).length
    // Every name in a quiet place; in a crowd only yours and the one you point at. NPCs when you're near.
    const allNames = o.names || players <= 12
    const named = (q: (typeof placed)[number]) => q.p.id === world.me || q.p.id === o.focus || (q.npc ? q.dist < 3.5 : allNames)
    for (const q of placed) {
      const h = this.heads.get(q.p.id)!
      if (named(q)) {
        if (q.npc) this.chip(h.x, h.y - 4, `${q.p.name} · ${this.roles.get(q.p.id) ?? 'local'}`, '#a39cbf', false, true)
        else this.chip(h.x, h.y - 4, q.p.name, blobArt(q.p.look).color, q.p.id === world.me)
      }
      if (q.p.pose === 'nap' && world.derive(q.p, now).state === 'nap') this.zzz(h.x + 14, h.y - 10, t + q.p.phase)
    }
    for (const q of placed) {
      const p = q.p
      if (!p.bubble) continue
      if (p.bubble.until < now) { p.bubble = undefined; continue }
      const h = this.heads.get(p.id)!
      const k = (now - p.bubble.at) / (p.bubble.until - p.bubble.at)
      this.bubble(h.x, h.y - (named(q) ? 22 : 6), p.bubble.text, k, p.id === world.me ? 'me' : q.npc ? 'npc' : 'other')
    }
  }

  /** Glints on the canals and the sea, and foam breathing along the shore. */
  private waterLive(view: Box, t: number, day: number): void {
    const ctx = this.ctx
    const surge = 0.5 + 0.5 * Math.sin(t * 0.9)
    for (const [x, y, shore] of this.water) {
      const p = iso(x + 0.5, y + 0.5, -0.3)
      if (p.x < view.x || p.x > view.x + view.w || p.y < view.y || p.y > view.y + view.h) continue
      const tw = Math.sin(t * 1.7 + x * 2.3 + y * 1.1)
      if (tw > 0.6) {
        ctx.fillStyle = `rgba(255,255,255,${(tw - 0.6) * 1.6 * (0.35 + 0.65 * day)})`
        ctx.fillRect(p.x - 4 + ((x * 7) % 9), p.y - 2 + ((y * 5) % 5), 7, 1.6)
      }
      if (shore) {
        const a = iso(x, y + 0.08 + surge * 0.18, -0.3), b = iso(x + 1, y + 0.08 + surge * 0.18, -0.3)
        ctx.strokeStyle = `rgba(255,255,255,${0.55 + 0.25 * surge})`; ctx.lineWidth = 3; ctx.lineCap = 'round'
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke()
      }
    }
  }

  private evictChunks(): void {
    const cached = this.chunks.filter((c) => c.cache)
    if (cached.length <= MAX_CHUNKS) return
    cached.sort((a, b) => a.cache!.used - b.cache!.used)
    for (const c of cached.slice(0, cached.length - MAX_CHUNKS)) c.cache = undefined
  }

  // ─── Pieces ─────────────────────────────────────────────────────────────

  private sky(day: number, t: number, r: RoomDef): void {
    const ctx = this.ctx
    const W = this.canvas.width, H = this.canvas.height
    const g = ctx.createLinearGradient(0, 0, 0, H)
    const tint = r.accent
    g.addColorStop(0, mix(mix('#171a3d', '#cfe6f7', day), tint, 0.06))
    g.addColorStop(0.6, mix(mix('#232150', '#e9eef6', day), tint, 0.08))
    g.addColorStop(1, mix(mix('#2e2653', '#f8eef0', day), tint, 0.1))
    ctx.fillStyle = g
    ctx.fillRect(0, 0, W, H)
    if (day < 0.85) {
      for (let i = 0; i < 80; i++) {
        const x = rnd(i, 1) * W, y = rnd(i, 2) * H * 0.9
        const tw = 0.55 + 0.45 * Math.sin(t / (0.5 + rnd(i, 3) * 0.9) + i)
        ctx.globalAlpha = (1 - day) * tw * (0.35 + rnd(i, 4) * 0.5)
        ctx.fillStyle = i % 7 === 0 ? '#ffe9b8' : '#ffffff'
        ctx.beginPath(); ctx.arc(x, y, (0.6 + rnd(i, 5) * 1.3) * this.dpr, 0, Math.PI * 2); ctx.fill()
      }
      ctx.globalAlpha = 1
    }
    if (day > 0.15) {
      for (let i = 0; i < 7; i++) {
        const cw = (60 + rnd(i, 21) * 90) * this.dpr
        const x = ((rnd(i, 22) * (W + cw * 2) + t * (6 + rnd(i, 23) * 8) * this.dpr) % (W + cw * 2)) - cw
        const y = rnd(i, 24) * H * 0.85
        ctx.globalAlpha = day * (0.35 + rnd(i, 25) * 0.3)
        ctx.fillStyle = '#ffffff'
        for (const [dx, dy, rr] of [[0, 0, 0.32], [0.28, -0.12, 0.26], [0.55, 0, 0.3], [0.3, 0.08, 0.3]] as const) {
          ctx.beginPath(); ctx.ellipse(x + dx * cw, y + dy * cw, rr * cw, rr * cw * 0.6, 0, 0, Math.PI * 2); ctx.fill()
        }
      }
      ctx.globalAlpha = 1
    }
  }

  private ring(x: number, y: number, z: number, color: string, t: number): void {
    const ctx = this.ctx
    const c = iso(x, y, z)
    const k = 0.5 + 0.5 * Math.sin(t * 3)
    ctx.save()
    ctx.strokeStyle = alpha(color, 0.55 + 0.3 * k)
    ctx.lineWidth = 2
    ctx.beginPath(); ctx.ellipse(c.x, c.y, 17 + k * 2, 8.5 + k, 0, 0, Math.PI * 2); ctx.stroke()
    ctx.restore()
  }

  private hoverTile(x: number, y: number): void {
    const g = grid(this.room!.id)
    if (cellAt(g, x, y) === 0) return
    poly(this.ctx, [iso(x, y, 0.01), iso(x + 1, y, 0.01), iso(x + 1, y + 1, 0.01), iso(x, y + 1, 0.01)], 'rgba(255,255,255,0.22)', 'rgba(255,255,255,0.7)', 1.5)
  }

  private zzz(x: number, y: number, t: number): void {
    const ctx = this.ctx
    ctx.save()
    ctx.font = `800 11px ${FONT}`
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.5 + i / 3) % 1
      ctx.globalAlpha = Math.sin(k * Math.PI)
      ctx.fillStyle = '#ffffff'
      ctx.strokeStyle = 'rgba(40,30,80,0.5)'; ctx.lineWidth = 3
      ctx.strokeText('z', x + k * 12, y - k * 22)
      ctx.fillText('z', x + k * 12, y - k * 22)
    }
    ctx.restore()
  }

  private chip(x: number, y: number, text: string, color: string, strong: boolean, npc = false): void {
    const ctx = this.ctx
    ctx.font = `${npc ? 'italic ' : ''}${strong ? 800 : 700} 11px ${FONT}`
    const w = ctx.measureText(text).width + 16
    roundRect(ctx, x - w / 2 + 1, y - 16, w, 17, 8.5)
    ctx.fillStyle = 'rgba(30,20,60,0.16)'
    ctx.fill()
    roundRect(ctx, x - w / 2, y - 17, w, 17, 8.5)
    ctx.fillStyle = strong ? shade(color, -0.42) : npc ? 'rgba(247,243,255,0.94)' : 'rgba(255,253,249,0.94)'
    ctx.fill()
    ctx.strokeStyle = strong ? '#ffffff' : color
    ctx.lineWidth = 1.6
    if (npc) ctx.setLineDash([3, 2])
    ctx.stroke()
    ctx.setLineDash([])
    ctx.fillStyle = strong ? '#ffffff' : shade(color, -0.5)
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, x, y - 8)
    ctx.textAlign = 'left'
  }

  private bubble(x: number, y: number, text: string, k: number, who: 'me' | 'npc' | 'other'): void {
    const ctx = this.ctx
    const fade = k < 0.04 ? k / 0.04 : k > 0.9 ? (1 - k) / 0.1 : 1
    ctx.save()
    ctx.globalAlpha = Math.max(0, Math.min(1, fade))
    ctx.font = `${who === 'npc' ? 'italic ' : ''}600 12.5px ${FONT}`
    const lines = wrapText(ctx, text, 190, 4)
    const w = Math.max(30, ...lines.map((l) => ctx.measureText(l).width)) + 20
    const h = lines.length * 16 + 12
    const pop = k < 0.05 ? 0.85 + 0.15 * (k / 0.05) : 1
    ctx.translate(x, y)
    ctx.scale(pop, pop)
    const bx = -w / 2, by = -h - 10
    const border = who === 'me' ? '#ffb3c7' : who === 'npc' ? '#cfc8e0' : '#c8bfff'
    roundRect(ctx, bx + 2, by + 3, w, h, 11)
    ctx.fillStyle = 'rgba(30,20,60,0.16)'; ctx.fill()
    roundRect(ctx, bx, by, w, h, 11)
    ctx.fillStyle = '#ffffff'; ctx.fill()
    ctx.strokeStyle = border; ctx.lineWidth = 2; ctx.stroke()
    ctx.beginPath(); ctx.moveTo(-6, by + h - 0.5); ctx.lineTo(0, by + h + 8); ctx.lineTo(6, by + h - 0.5); ctx.closePath()
    ctx.fillStyle = '#ffffff'; ctx.fill()
    ctx.beginPath(); ctx.moveTo(-6, by + h); ctx.lineTo(0, by + h + 8); ctx.lineTo(6, by + h); ctx.stroke()
    ctx.fillStyle = '#3a3552'
    ctx.textBaseline = 'top'
    lines.forEach((l, i) => ctx.fillText(l, bx + 10, by + 7 + i * 16))
    ctx.restore()
  }
}
